import { LedgerAmount, LedgerBookingRow, LedgerRow } from '../components/account-ledger/account-ledger.types';
import { IAccountLedgerPage, ILedgerMoney, ILedgerRowWire } from '../models/account-ledger.model';
import type { IClosingNote } from '../services/closing-note.service';
import { displaySignOf, inDisplaySign, toUnits } from './accounts-presentation';

/** `2025-06-15` → `15.06.2025`, from the calendar date itself — no time zone can move it. */
export function ledgerDate(iso: string): string {
    const [year, month, day] = iso.slice(0, 10).split('-');
    return year !== undefined && month !== undefined && day !== undefined ? `${day}.${month}.${year}` : iso;
}

const bare = (money: ILedgerMoney): LedgerAmount => ({ value: toUnits(Math.abs(money.amount)) });

/**
 * The Kontenblatt's view rows: the opening row, the bookings, and the closing row — the closing only
 * once the last page is loaded, because before that it would stand under rows that are not its own.
 *
 * The balances follow the account header's sign (`displaySignOf`): positive on a liability or equity
 * account, an income-statement account as its effect on the result — so the closing balance and the
 * header's ERP balance read as one figure.
 */
export function ledgerRowsOf(
    page: Omit<IAccountLedgerPage, 'rows'> | null,
    rows: readonly ILedgerRowWire[],
    canLoadMore: boolean,
    commentedLineIds: ReadonlySet<string> = new Set(),
    accountGroup: string | null = null,
    accountNumber?: string,
): LedgerRow[] {
    if (page === null || page.opening === null || page.closing === null) {
        return [];
    }
    const sign = displaySignOf(accountGroup, accountNumber);
    const withCurrency = (money: ILedgerMoney): LedgerAmount => ({
        value: inDisplaySign(toUnits(money.amount), sign),
        currency: money.currency,
    });
    // An account with neither a posting nor a carried-forward balance shows the empty state.
    if (rows.length === 0 && page.opening.base.amount === 0) {
        return [];
    }
    const unavailable = (foreign: ILedgerMoney | null): string | null =>
        page.foreignCurrency !== null && foreign === null ? page.foreignCurrency : null;

    const opening: LedgerRow = {
        kind: 'title',
        id: 'opening',
        date: page.period === null ? '' : ledgerDate(page.period.periodStart),
        variant: 'opening',
        balance: withCurrency(page.opening.base),
        foreignBalance: page.opening.foreign === null ? null : withCurrency(page.opening.foreign),
        foreignUnavailable: unavailable(page.opening.foreign),
    };
    const bookings: LedgerRow[] = rows.map((row) => ({
        kind: 'booking',
        id: row.id,
        date: ledgerDate(row.date),
        text: row.description ?? '',
        accountNumber: row.contraAccountNumber,
        accountLabel: row.contraAccountName ?? '',
        ...(row.vatCode !== null ? { vatCode: row.vatCode } : {}),
        debit: row.side === 'DEBIT' ? bare(row.amount) : null,
        credit: row.side === 'CREDIT' ? bare(row.amount) : null,
        balance: withCurrency(row.balance),
        foreign:
            row.foreign === null
                ? null
                : {
                      amount: { ...bare(row.foreign.amount), currency: row.foreign.amount.currency },
                      rate: row.foreign.rate,
                      side: row.side === 'DEBIT' ? 'debit' : 'credit',
                  },
        foreignBalance: row.foreignBalance === null ? null : withCurrency(row.foreignBalance),
        hasComment: commentedLineIds.has(row.id),
    }));
    if (canLoadMore) {
        return [opening, ...bookings];
    }
    const closing: LedgerRow = {
        kind: 'title',
        id: 'closing',
        date: page.period === null ? '' : ledgerDate(page.period.periodEnd),
        variant: 'closing',
        balance: withCurrency(page.closing.base),
        foreignBalance: page.closing.foreign === null ? null : withCurrency(page.closing.foreign),
        foreignUnavailable: unavailable(page.closing.foreign),
    };
    return [opening, ...bookings, closing];
}

/** The Kontenblatt lines the account's comments quote. A to-do never marks a line. */
export function commentedLineIdsOf(notes: readonly IClosingNote[]): Set<string> {
    const ids = new Set<string>();
    for (const note of notes) {
        const blockId = note.anchor?.blockId ?? null;
        if (note.kind === 'COMMENT' && blockId !== null) {
            ids.add(blockId);
        }
    }
    return ids;
}

/** A comment's quote of a booking line: `05.01.2026 · Amortisation Hypothek · 5'000.00`. */
export function ledgerQuoteOf(row: LedgerBookingRow, formatAmount: (value: number) => string): string {
    const amount = row.debit ?? row.credit ?? null;
    return [row.date, row.text, amount === null ? '' : formatAmount(amount.value)]
        .filter((part) => part.length > 0)
        .join(' · ');
}

export type LedgerJumpStep = 'found' | 'wait' | 'load-more' | 'missing' | 'unavailable';

/**
 * What to do next to show a line a comment quotes. The line may sit on a page not loaded yet; it is
 * missing only once every page is in.
 */
export function ledgerJumpStep(
    ledger: {
        rows: readonly LedgerRow[];
        loaded: boolean;
        loading: boolean;
        failed: boolean;
        canLoadMore: boolean;
    },
    lineId: string,
): LedgerJumpStep {
    if (ledger.rows.some((row) => row.id === lineId)) {
        return 'found';
    }
    // The Kontenblatt shows its own error; a message on top would blame the line.
    if (ledger.failed) {
        return 'unavailable';
    }
    if (!ledger.loaded || ledger.loading) {
        return 'wait';
    }
    return ledger.canLoadMore ? 'load-more' : 'missing';
}
